import {
  Fragment,
  useState,
} from 'react';
import {
  Callout,
  DateInput,
  Field,
  Modal,
  Select,
  baseCurrencyOptions,
  TextInput,
  Chip,
  Empty,
  Loading,
  TableScroll,
  explainFailure,
  formatDateTime,
  scheduledStartFor,
  todayIso,
  useLoad,
  SortTh,
  Sorted,
  sortableCell,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import {
  brandSettingGroups,
  formatSettingValue,
  scheduledChangeLine,
  settingHistoryStateLabel,
  settingHistoryTone,
} from '../../screens.ts';
import { Failure } from '../shared.tsx';
import { brandProfileChanges, proposableBrandChange, type BrandProfileDraft } from '../direct-edit-model.ts';
import { useAbilities } from '../abilities.tsx';
import { t } from '../../messages.ts';

/**
 * One brand, and everything the platform can honestly read about it.
 *
 * Two reads, both of them ones this console already had:
 *
 *  - the operator's own dashboard query, narrowed to this brand. The route has
 *    always parsed `brand_id` beside the date range and `overview` has always
 *    filtered every figure by it; this console simply never asked. No new
 *    route, and no figure invented.
 *  - the operator's affiliate list, filtered to the ones enrolled on this
 *    brand. That list already carries each affiliate's enrollments, and there
 *    is no brand-scoped affiliate read on the platform side to call instead.
 *
 * What is deliberately absent: this brand's campaigns, its affiliate invoices
 * and its disputes. All three exist server-side and all three take a brand
 * filter — and all three are the operator console's own routes, which scope
 * themselves to the caller's operator and have no `operator_id` handshake for
 * platform staff. Reaching them from here is a route decision, not a side
 * effect of a screen, so they are named in the report rather than rendered as
 * a section that would only ever be empty.
 *
 * One list, so one scroll. The left-nav sectioning the operator popup uses
 * earns itself at seven sections; at a fact grid, a figure grid and a single
 * table it would be navigation around a page that fits on one.
 *
 * Both reads are cross-tenant and both are logged per access, which is why
 * they happen when a reader opens one brand rather than for every row of the
 * table behind it.
 */
/**
 * §3.9d — one brand's settings, and a proposal to change one.
 *
 * The values are readable here because `config_values` grants platform staff
 * SELECT: support answering "why is this brand holding CPAs for sixty days"
 * should not have to ask the operator to read their own screen to them. The
 * change is a proposal for the same reason every other write from this console
 * is one — a brand's hold, its attribution window, what its revenue share is a
 * share of, are commercial terms the operator answers for to their affiliates,
 * and the audit record has to name the person who chose.
 *
 * Every setting is shown, including the ones the brand inherits, because the
 * common support question is "what is it actually on" and an inherited value
 * is an answer to that.
 */
export function BrandSettings({
  api,
  operatorId,
  brandId,
  canWrite,
}: {
  api: PlatformApi;
  operatorId: string;
  brandId: string;
  canWrite: boolean;
}) {
  const [version, setVersion] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const [proposed, setProposed] = useState<string | null>(null);
  const { can } = useAbilities();
  /** §12.2 — which setting's History dialog is open. Not gated by `canWrite`:
      reading history is not a write control. */
  const [historyKey, setHistoryKey] = useState<string | null>(null);
  const loaded = useLoad(() => api.brandSettings(operatorId, brandId), [operatorId, brandId, version]);
  if (loaded.state === 'loading') return <Loading rows={3} />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  const source = (row: { resolved_from: string; inherited_from?: string }): string =>
    row.resolved_from === 'BRAND'
      ? t('brand.settings.setForBrand')
      : row.inherited_from === 'OPERATOR'
        ? t('brand.settings.inheritedOperator')
        : t('brand.settings.inheritedPlatform');
  const chosen = loaded.value.items.find((r) => r.key === open);
  // §12.2 — the five click/bot keys read together under their own heading,
  // when they are actually there to group; brandSettingGroups is a no-op
  // single group otherwise.
  const groups = brandSettingGroups(loaded.value.items);

  return (
    <>
      {/* Batch 2 review, finding 7 — the result of the proposal just made;
          read whole. */}
      {proposed ? <Callout fold={false}>{proposed}</Callout> : null}
      {groups.map((group) => (
        <Fragment key={group.title ?? '_ungrouped'}>
          {group.title ? <h3 className="subhead">{group.title}</h3> : null}
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('brand.settings.colSetting')}</th>
                  <th className="num">{t('brand.settings.colValue')}</th>
                  <th className="col-action" />
                </tr>
              </thead>
              <tbody>
                {group.items.map((row) => (
                  <tr key={row.key}>
                    <td>
                      {/* Where the value came from belongs to the setting, not
                          to a column of its own beside it. */}
                      <div className="wrap">{row.description}</div>
                      <div className="faint">{source(row)}</div>
                      <code className="setting-key">{row.key}</code>
                    </td>
                    <td className="num">
                      {formatSettingValue(row.key, row.value)}
                      {/* §12.2 — a change already on its way, shown under the
                          value in force so it is seen before it lands. */}
                      {row.scheduled ? (
                        <div className="faint">
                          {scheduledChangeLine(
                            formatSettingValue(row.key, row.scheduled.value),
                            row.scheduled.effective_from,
                          )}
                        </div>
                      ) : null}
                    </td>
                    <td className="num col-action">
                      <div className="stack">
                        {canWrite ? (
                          <button
                            type="button"
                            className="copy"
                            onClick={() => { setProposed(null); setOpen(row.key); }}
                          >
                            {can.edit ? t('brand.settings.change') : t('brand.settings.propose')}
                          </button>
                        ) : null}
                        <button type="button" className="link-button" onClick={() => setHistoryKey(row.key)}>
                          {t('brand.settings.history')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </Fragment>
      ))}
      {chosen ? (
        <Modal title={chosen.key} onClose={() => setOpen(null)}>
          <ProposeBrandSetting
            api={api}
            operatorId={operatorId}
            brandId={brandId}
            row={chosen}
            today={todayIso()}
            onDone={(note) => {
              setOpen(null);
              setProposed(note);
              setVersion((v) => v + 1);
            }}
          />
        </Modal>
      ) : null}
      {historyKey ? (
        <SettingHistoryModal
          api={api}
          operatorId={operatorId}
          brandId={brandId}
          settingKey={historyKey}
          onClose={() => setHistoryKey(null)}
        />
      ) : null}
    </>
  );
}

/** §3.9d / §12.2 — the proposal itself: a value, when it applies, a reason, and nothing that takes effect. */
function ProposeBrandSetting({
  api,
  operatorId,
  brandId,
  row,
  today,
  onDone,
}: {
  api: PlatformApi;
  operatorId: string;
  brandId: string;
  row: {
    key: string;
    description: string;
    kind: 'number' | 'string' | 'boolean' | 'object';
    value: unknown;
    resolved_from: string;
    inherited_value?: unknown;
    options?: readonly string[];
    /** A change already on its way at this level, so the same day replaces it. */
    scheduled?: { readonly effective_from: string };
  };
  /** The reader's own today, `YYYY-MM-DD` — what "Applies from" defaults to. */
  today: string;
  onDone: (note: string) => void;
}) {
  const asText = (v: unknown): string =>
    v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
  const [value, setValue] = useState(asText(row.value));
  // §12.2 — defaults to today; sent as `effective_from` only when it is not,
  // the same "absent or today means now" rule `changeSetting` itself uses.
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  // Finding 2 — `clear` (below) is always immediate: it sends no
  // `effective_from`, so a date chosen above and then "cleared" would apply
  // on approval, not on that date. True whenever the field holds something
  // other than today, the same blank-or-today test `propose` itself uses to
  // decide whether to send `effective_from` at all.
  const clearBlockedByDate = effectiveFrom.trim() !== '' && effectiveFrom !== today;

  const parse = (raw: string): unknown => {
    if (row.kind === 'number') return Number(raw);
    if (row.kind === 'boolean') return raw === 'true';
    if (row.kind === 'object') { try { return JSON.parse(raw); } catch { return raw; } }
    return raw;
  };
  // §3.9d — whoever holds the edit capability changes the setting now; the
  // proposal stays as the other way, for a change the operator should agree.
  const { can } = useAbilities();
  const saveNow = (change: { value: unknown; effective_from?: string } | { clear: true }) => {
    setBusy(true);
    setFailure(null);
    void api
      .changeBrandSetting(operatorId, brandId, row.key, change, reason.trim())
      .then(() => onDone(t('brand.settings.savedNow', { key: row.key })))
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(false));
  };
  const propose = (payload: Record<string, unknown>) => {
    setBusy(true);
    setFailure(null);
    void api
      .proposeChange(operatorId, {
        action_type: 'BRAND_SETTING',
        target_id: brandId,
        payload: { key: row.key, ...payload },
        reason: reason.trim(),
      })
      .then((r) => onDone(r.message))
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(false));
  };

  return (
    <div className="form">
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      <p>{row.description}</p>
      <Field label={t('brand.propose.valueField')}>
        {row.options ? (
          <Select value={value} onChange={setValue} options={row.options.map((o) => ({ value: o, label: o }))} />
        ) : row.kind === 'boolean' ? (
          <Select
            value={value}
            onChange={setValue}
            options={[{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }]}
          />
        ) : (
          <TextInput value={value} onChange={setValue} />
        )}
      </Field>
      <Field label={t('brand.propose.appliesFrom')} hint={t('brand.propose.appliesHint')}>
        <DateInput value={effectiveFrom} onChange={setEffectiveFrom} min={today} today={today} />
      </Field>
      <Field
        label={t('brand.propose.why')}
        hint={t('brand.propose.whyHint')}
      >
        <TextInput value={reason} onChange={setReason} placeholder={t('brand.propose.whyPlaceholder')} />
      </Field>
      <div className="form-actions">
        {can.edit ? (
          <button
            className="primary"
            disabled={busy || reason.trim() === '' || value.trim() === ''}
            onClick={() => saveNow({
              value: parse(value),
              ...(effectiveFrom.trim() !== '' && effectiveFrom !== today ? { effective_from: scheduledStartFor(effectiveFrom, row.scheduled?.effective_from) } : {}),
            })}
          >
            {busy ? t('brand.settings.saving') : t('brand.settings.saveNow')}
          </button>
        ) : null}
        <button
          className={can.edit ? 'copy' : 'primary'}
          disabled={busy || reason.trim() === '' || value.trim() === ''}
          onClick={() => propose({
            value: parse(value),
            ...(effectiveFrom.trim() !== '' && effectiveFrom !== today ? { effective_from: scheduledStartFor(effectiveFrom, row.scheduled?.effective_from) } : {}),
          })}
        >
          {busy ? t('brand.propose.proposing') : can.edit ? t('brand.settings.proposeInstead') : t('brand.propose.value')}
        </button>
        {/* The way back, proposed the same way: a brand that holds its own
            value can be put back on the operator's. Clearing is immediate-only
            (§4.2c) — it sends no `effective_from` and applies on approval, not
            on the date chosen above — so it is disabled rather than silently
            ignoring a date the reader picked but did not mean to skip. */}
        {row.resolved_from === 'BRAND' ? (
          <button
            type="button"
            className="copy"
            disabled={busy || reason.trim() === '' || clearBlockedByDate}
            onClick={() => (can.edit ? saveNow({ clear: true }) : propose({ clear: true }))}
          >
            {t('brand.propose.inherit', { value: asText(row.inherited_value) || '—' })}
          </button>
        ) : null}
      </div>
      {row.resolved_from === 'BRAND' && clearBlockedByDate ? (
        <p className="panel-note">
          {t('brand.propose.clearBlocked')}
        </p>
      ) : null}
      <Callout tone="caution">
        {can.edit ? t('brand.settings.saveNowNote') : t('brand.propose.noChange')}
      </Callout>
    </div>
  );
}

/**
 * §4.2e / §12.2 — one setting's own history at this brand, including the
 * operator's own rows (`brandSettingHistory`'s own reasoning): every value it
 * has held, who set it, why, and whether it was withdrawn before it ever
 * started.
 */
function SettingHistoryModal({
  api,
  operatorId,
  brandId,
  settingKey,
  onClose,
}: {
  api: PlatformApi;
  operatorId: string;
  brandId: string;
  settingKey: string;
  onClose: () => void;
}) {
  const loaded = useLoad(
    () => api.brandSettingHistory(operatorId, brandId, settingKey),
    [operatorId, brandId, settingKey],
  );

  return (
    <Modal wide title={t('brand.history.title', { key: settingKey })} onClose={onClose}>
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
      {loaded.state === 'loading' ? <Loading rows={2} /> : null}
      {loaded.state === 'ready' ? (
        loaded.value.items.length === 0 ? (
          <Empty>{t('brand.history.empty')}</Empty>
        ) : (
          <>
            <Sorted rows={loaded.value.items} by={{ valid_from: (row) => sortableCell(row.valid_from), state: (row) => sortableCell(row.state), value: (row) => sortableCell(row.value), level: (row) => sortableCell(row.level), set_by: (row) => sortableCell(row.set_by), reason: (row) => sortableCell(row.reason) }}>
              {(ordered, sort) => (
            <TableScroll>
              <table>
                <thead>
                  <tr>
                    <SortTh sort={sort} col="valid_from">{t('brand.history.colFrom')}</SortTh>
                    {/* Finding 5 — next to From rather than last, so the state
                        that tells a withdrawn row apart from an ongoing one
                        is not the column horizontal scroll hides first at
                        1440. */}
                    <SortTh sort={sort} col="state">{t('brand.history.colState')}</SortTh>
                    <SortTh sort={sort} col="state">{t('brand.history.colUntil')}</SortTh>
                    <SortTh sort={sort} col="value" align="num">{t('brand.history.colValue')}</SortTh>
                    <SortTh sort={sort} col="level">{t('brand.history.colLevel')}</SortTh>
                    <SortTh sort={sort} col="set_by">{t('brand.history.colSetBy')}</SortTh>
                    <SortTh sort={sort} col="reason">{t('brand.history.colWhy')}</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((row) => (
                    <tr key={row.id}>
                      {/* Owner, 2026-10-12 — an instant, shown in the reader's
                          own zone like every other time here. */}
                      <td className="faint">
                        {formatDateTime(row.valid_from)}
                      </td>
                      <td>
                        <Chip tone={settingHistoryTone(row.state)}>{settingHistoryStateLabel(row.state)}</Chip>
                      </td>
                      {/* Finding 5 — a row withdrawn before it ever started
                          holds `valid_to: null` same as a row still ongoing;
                          the two must not read the same. */}
                      <td className="faint">
                        {row.state === 'WITHDRAWN' && row.withdrawn_at
                          ? t('brand.history.withdrawn', { when: formatDateTime(row.withdrawn_at), who: row.withdrawn_by ?? '—' })
                          : row.valid_to
                            ? formatDateTime(row.valid_to)
                            : t('brand.history.ongoing')}
                      </td>
                      <td className="num">{formatSettingValue(settingKey, row.value)}</td>
                      <td>{row.level === 'BRAND' ? t('brand.history.levelBrand') : t('brand.history.levelOperator')}</td>
                      <td className="faint">{row.set_by ?? '—'}</td>
                      <td className="wrap">{row.reason ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
              )}
            </Sorted>
          </>
        )
      ) : null}
    </Modal>
  );
}

/**
 * §3.9d — a brand's profile: its name, base currency and default page.
 *
 * Whoever holds the edit capability saves it now (PATCH on the operator's own
 * route, audited under their platform role and told to the operator); the
 * proposal stays beside it as the other way, for a change the operator
 * should agree to first. A proposal carries the name and currency only, so
 * it is off while the default page is part of the change.
 *
 * Every field starts from what the brand currently is, and only what changed
 * is sent — a form that starts empty invites somebody to fill in one field
 * and silently blank the other.
 */
export function ProposeBrandChange({
  api,
  operatorId,
  brand,
  onDone,
  onSaved,
}: {
  api: PlatformApi;
  operatorId: string;
  brand: { brand_id: string; display_name: string; base_currency: string; default_url?: string | null };
  /** A proposal was sent: the server's own sentence about it. */
  onDone: (note: string) => void;
  /** Saved now: the brand as it is after the save, and what to tell the reader. */
  onSaved: (p: { display_name: string; base_currency: string; default_url: string | null }, note: string) => void;
}) {
  const original: BrandProfileDraft = {
    display_name: brand.display_name,
    base_currency: brand.base_currency,
    default_url: brand.default_url ?? '',
  };
  const [draft, setDraft] = useState<BrandProfileDraft>(original);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const { can } = useAbilities();

  const changes = brandProfileChanges(original, draft);
  const blocked = busy || changes === null || reason.trim() === '' || draft.display_name.trim() === '';

  const finish = (p: Promise<unknown>, after: (r: unknown) => void) => {
    setBusy(true);
    setFailure(null);
    void p
      .then((r) => { setReason(''); after(r); })
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(false));
  };

  return (
    <>
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      <div className="form">
        <div className="form-row">
          <Field label={t('brand.profile.displayName')}>
            <TextInput value={draft.display_name} onChange={(v) => setDraft((d) => ({ ...d, display_name: v }))} />
          </Field>
          <Field label={t('brand.profile.baseCurrency')} hint={t('brand.profile.currencyHint')}>
            <Select
              value={draft.base_currency}
              onChange={(v) => setDraft((d) => ({ ...d, base_currency: v }))}
              options={baseCurrencyOptions()}
            />
          </Field>
          <Field label={t('ent.brand.defaultUrl')} hint={t('edit.brand.defaultUrlHint')}>
            <TextInput value={draft.default_url} onChange={(v) => setDraft((d) => ({ ...d, default_url: v }))} />
          </Field>
        </div>
        <Field label={t('brand.profile.why')} hint={t('brand.profile.whyHint')}>
          <TextInput value={reason} onChange={setReason} placeholder={t('brand.profile.whyPlaceholder')} />
        </Field>
        <div className="form-actions">
          {can.edit ? (
            <button
              type="button"
              className="primary"
              disabled={blocked}
              onClick={() => finish(
                api.updateBrandProfile(operatorId, brand.brand_id, changes ?? {}, reason.trim()),
                () => onSaved(
                  {
                    display_name: draft.display_name.trim(),
                    base_currency: draft.base_currency,
                    default_url: draft.default_url.trim() === '' ? null : draft.default_url.trim(),
                  },
                  t('edit.brand.saved', { name: draft.display_name.trim() }),
                ),
              )}
            >
              {busy ? t('brand.settings.saving') : t('brand.settings.saveNow')}
            </button>
          ) : null}
          <button
            type="button"
            className={can.edit ? 'copy' : 'primary'}
            disabled={blocked || !proposableBrandChange(changes)}
            title={changes && !proposableBrandChange(changes) ? t('edit.brand.urlNotProposable') : undefined}
            onClick={() => finish(
              api.proposeChange(operatorId, {
                action_type: 'BRAND_PROFILE',
                target_id: brand.brand_id,
                payload: Object.fromEntries(Object.entries(changes ?? {}).filter(([k]) => k !== 'default_url')),
                reason: reason.trim(),
              }),
              (r) => onDone((r as { message: string }).message),
            )}
          >
            {busy ? t('brand.profile.proposing') : can.edit ? t('brand.settings.proposeInstead') : t('brand.profile.propose')}
          </button>
        </div>
        <Callout tone="caution">
          {can.edit ? t('edit.brand.saveNowNote') : t('brand.profile.noChange')}
        </Callout>
      </div>
    </>
  );
}
